import { z } from 'zod';
import { companyIdSchema } from './company';

const isoDateTimeSchema = z.string().datetime({
  error: 'La date doit être au format ISO 8601.',
});

const nonNegativeIntSchema = z.number().int().nonnegative();

export const kbSourceTypeSchema = z.enum(['m365_sharepoint'], {
  error: 'Le type de source documentaire est invalide.',
});

export const kbSourceStatusSchema = z.enum([
  'pending_consent',
  'connected',
  'syncing',
  'error',
  'disabled',
], {
  error: 'Le statut de la source documentaire est invalide.',
});

export const kbSourceDriveSyncStatusSchema = z.enum(['idle', 'syncing', 'error'], {
  error: 'Le statut de synchronisation du drive est invalide.',
});

export const kbSourceDriveResyncStateSchema = z.enum(['none', 'staged'], {
  error: 'L’état de resynchronisation du drive est invalide.',
});

export const kbSourceActionSchema = z.enum([
  'pause',
  'resume',
  'resync',
  'disable',
], {
  error: 'L’action sur la source documentaire est invalide.',
});

export const m365CreateSourceSchema = z.object({
  displayName: z.string().trim().min(1, {
    error: 'Le nom de la source Microsoft 365 est requis.',
  }).max(255, {
    error: 'Le nom de la source Microsoft 365 est trop long.',
  }),
  entraTenantId: z.uuid({
    error: 'L’identifiant du tenant Entra est invalide.',
  }),
});

export const kbSourceParamsSchema = z.object({
  sourceId: z.uuid({
    error: 'L’identifiant de la source documentaire est invalide.',
  }),
});

export const m365AddSiteSchema = z.object({
  siteUrl: z.url({
    error: 'L’URL du site SharePoint est invalide.',
  }).max(2_048, {
    error: 'L’URL du site SharePoint est trop longue.',
  }),
});

export const m365AdminConsentCallbackQuerySchema = z.object({
  admin_consent: z.string().trim().max(16).optional(),
  error: z.string().trim().max(120).optional(),
  error_description: z.string().max(4_000).optional(),
  scope: z.string().max(4_000).optional(),
  state: z.string().trim().min(1, {
    error: 'L’état de consentement Microsoft 365 est requis.',
  }).max(2_048, {
    error: 'L’état de consentement Microsoft 365 est invalide.',
  }),
  tenant: z.uuid({
    error: 'L’identifiant du tenant Entra retourné est invalide.',
  }).optional(),
  tid: z.uuid({
    error: 'L’identifiant du tenant Entra retourné est invalide.',
  }).optional(),
}).superRefine((value, ctx) => {
  if (value.tenant && value.tid && value.tenant !== value.tid) {
    ctx.addIssue({
      code: 'custom',
      message: 'Les identifiants de tenant Entra retournés sont incohérents.',
      path: ['tid'],
    });
  }

  if (!value.error && !value.tenant && !value.tid) {
    ctx.addIssue({
      code: 'custom',
      message: 'L’identifiant du tenant Entra retourné est requis.',
      path: ['tenant'],
    });
  }
});

export const m365SourceSummarySchema = z.object({
  companyId: companyIdSchema,
  displayName: z.string().trim().min(1).max(255),
  entraTenantId: z.uuid(),
  id: z.uuid(),
  status: kbSourceStatusSchema,
  type: kbSourceTypeSchema,
});

export const m365SourceResponseSchema = z.object({
  data: m365SourceSummarySchema,
  success: z.literal(true),
});

export const m365AdminConsentUrlResponseSchema = z.object({
  data: z.object({
    adminConsentUrl: z.url(),
    expiresAt: isoDateTimeSchema,
    sourceId: z.uuid(),
  }),
  success: z.literal(true),
});

export const m365AdminConsentCallbackResponseSchema = z.object({
  data: z.object({
    consentValidated: z.literal(true),
    source: m365SourceSummarySchema,
  }),
  success: z.literal(true),
});

export const m365CapacityAdmissionSchema = z.object({
  aclRecrawlWindowMinutes: z.number().int().positive(),
  contentRuMarginRatio: z.number().min(0).max(1),
  documentCap: z.number().int().nonnegative(),
  existingDocumentCount: z.number().int().nonnegative(),
  modelVersion: z.literal(1),
  permissionReadRu: z.number().int().positive(),
  projectedDocumentCount: z.number().int().nonnegative(),
  ruBudgetPerMinute: z.number().int().positive(),
  siteEstimatedDocumentCount: z.number().int().nonnegative(),
});

export const m365SourceDriveSummarySchema = z.object({
  driveId: z.string().trim().min(1),
  id: z.uuid(),
  siteId: z.string().trim().min(1),
  sourceId: z.uuid(),
});

export const m365AddSiteResponseSchema = z.object({
  data: z.object({
    capacity: m365CapacityAdmissionSchema,
    created: z.boolean(),
    drives: z.array(m365SourceDriveSummarySchema),
    site: z.object({
      estimatedDocumentCount: z.number().int().nonnegative(),
      id: z.string().trim().min(1),
      url: z.url(),
    }),
    source: m365SourceSummarySchema,
  }),
  success: z.literal(true),
});

export const m365LifecycleEventSchema = z.enum([
  'missed',
  'reauthorizationRequired',
  'subscriptionRemoved',
], {
  error: 'L’événement de cycle de vie Microsoft Graph est invalide.',
});

export const m365LifecycleNotificationSchema = z.object({
  clientState: z.string().trim().min(1).max(255),
  lifecycleEvent: m365LifecycleEventSchema,
  subscriptionExpirationDateTime: isoDateTimeSchema.optional(),
  subscriptionId: z.string().trim().min(1).max(255),
  tenantId: z.uuid({
    error: 'L’identifiant du tenant Entra de la notification est invalide.',
  }),
});

export const m365LifecycleNotificationCollectionSchema = z.object({
  value: z.array(m365LifecycleNotificationSchema).min(1).max(100),
});

export const m365ContentNotificationSchema = z.object({
  changeType: z.literal('updated').optional(),
  clientState: z.string().trim().min(1).max(128),
  resource: z.string().trim().min(1).max(2_048).optional(),
  subscriptionExpirationDateTime: isoDateTimeSchema.optional(),
  subscriptionId: z.string().trim().min(1).max(255),
  tenantId: z.uuid({
    error: 'L’identifiant du tenant Entra de la notification est invalide.',
  }).optional(),
});

export const m365ContentNotificationCollectionSchema = z.object({
  value: z.array(m365ContentNotificationSchema).min(1).max(100),
});

export const m365WebhookValidationQuerySchema = z.object({
  validationToken: z.string().max(4_096).optional(),
});

export const externalIdentityProviderSchema = z.enum(['entra'], {
  error: 'Le fournisseur d’identité externe est invalide.',
});

export const externalIdentityMatchMethodSchema = z.enum(['email', 'sso'], {
  error: 'La méthode de rapprochement d’identité est invalide.',
});

export const kbSourceSchema = z.object({
  companyId: companyIdSchema,
  configJson: z.record(z.string(), z.unknown()),
  createdAt: isoDateTimeSchema,
  deletedAt: isoDateTimeSchema.nullable(),
  displayName: z.string().trim().min(1).max(255),
  documentCount: nonNegativeIntSchema,
  entraTenantId: z.uuid({
    error: 'L’identifiant du tenant Entra est invalide.',
  }),
  failedDocumentCount: nonNegativeIntSchema,
  id: z.uuid({
    error: 'L’identifiant de la source documentaire est invalide.',
  }),
  lastError: z.string().nullable(),
  lastSyncAt: isoDateTimeSchema.nullable(),
  pausedAt: isoDateTimeSchema.nullable(),
  status: kbSourceStatusSchema,
  type: kbSourceTypeSchema,
  unresolvedAclCount: nonNegativeIntSchema,
  updatedAt: isoDateTimeSchema,
});

export const kbSourceDriveSchema = z.object({
  createdAt: isoDateTimeSchema,
  deltaLink: z.string().nullable(),
  driveId: z.string().trim().min(1),
  id: z.uuid({
    error: 'L’identifiant interne du drive est invalide.',
  }),
  lockedAt: isoDateTimeSchema.nullable(),
  lockedBy: z.string().trim().min(1).nullable(),
  resyncState: kbSourceDriveResyncStateSchema,
  siteId: z.string().trim().min(1),
  sourceId: z.uuid({
    error: 'L’identifiant de la source documentaire est invalide.',
  }),
  subscriptionExpiresAt: isoDateTimeSchema.nullable(),
  subscriptionId: z.string().trim().min(1).nullable(),
  syncStatus: kbSourceDriveSyncStatusSchema,
  updatedAt: isoDateTimeSchema,
  version: z.number().int().positive(),
});

export const m365SourceCapacitySummarySchema = z.object({
  documentCap: nonNegativeIntSchema,
  existingDocumentCount: nonNegativeIntSchema,
  projectedDocumentCount: nonNegativeIntSchema,
});

export const m365SourceHealthDriveSchema = z.object({
  driveId: z.string().trim().min(1),
  id: z.uuid({
    error: 'L’identifiant interne du drive est invalide.',
  }),
  resyncState: kbSourceDriveResyncStateSchema,
  siteId: z.string().trim().min(1),
  subscriptionExpiresAt: isoDateTimeSchema.nullable(),
  syncStatus: kbSourceDriveSyncStatusSchema,
});

export const m365SourceHealthSchema = z.object({
  capacity: m365SourceCapacitySummarySchema,
  consentValidated: z.boolean(),
  displayName: z.string().trim().min(1).max(255),
  documentCount: nonNegativeIntSchema,
  drives: z.array(m365SourceHealthDriveSchema),
  failedDocumentCount: nonNegativeIntSchema,
  id: z.uuid({
    error: 'L’identifiant de la source documentaire est invalide.',
  }),
  lastError: z.string().trim().min(1).max(500).nullable(),
  lastSyncAt: isoDateTimeSchema.nullable(),
  pausedAt: isoDateTimeSchema.nullable(),
  status: kbSourceStatusSchema,
  type: kbSourceTypeSchema,
  unresolvedAclCount: nonNegativeIntSchema,
});

export const m365SourcesHealthResponseSchema = z.object({
  data: z.array(m365SourceHealthSchema),
  success: z.literal(true),
});

export const m365SourceHealthResponseSchema = z.object({
  data: m365SourceHealthSchema,
  success: z.literal(true),
});

export const m365SourceActionStateSchema = z.object({
  id: z.uuid({
    error: 'L’identifiant de la source documentaire est invalide.',
  }),
  pausedAt: isoDateTimeSchema.nullable(),
  status: kbSourceStatusSchema,
});

export const m365SourceActionResponseSchema = z.object({
  data: z.object({
    action: kbSourceActionSchema,
    changed: z.boolean(),
    source: m365SourceActionStateSchema,
  }),
  success: z.literal(true),
});

export const sourceActionInvalidStateDetailsSchema = z.object({
  action: kbSourceActionSchema,
  status: kbSourceStatusSchema,
});

export type KbSourceType = z.infer<typeof kbSourceTypeSchema>;
export type KbSourceStatus = z.infer<typeof kbSourceStatusSchema>;
export type KbSourceDriveSyncStatus = z.infer<typeof kbSourceDriveSyncStatusSchema>;
export type KbSourceDriveResyncState = z.infer<typeof kbSourceDriveResyncStateSchema>;
export type KbSourceAction = z.infer<typeof kbSourceActionSchema>;
export type ExternalIdentityProvider = z.infer<typeof externalIdentityProviderSchema>;
export type ExternalIdentityMatchMethod = z.infer<typeof externalIdentityMatchMethodSchema>;
export type KbSourceDTO = z.infer<typeof kbSourceSchema>;
export type KbSourceDriveDTO = z.infer<typeof kbSourceDriveSchema>;
export type M365SourceHealthDTO = z.infer<typeof m365SourceHealthSchema>;
export type M365SourceActionResponseDTO = z.infer<typeof m365SourceActionResponseSchema>;
export type M365CreateSourceDTO = z.infer<typeof m365CreateSourceSchema>;
export type M365AddSiteDTO = z.infer<typeof m365AddSiteSchema>;
export type M365CapacityAdmissionDTO = z.infer<typeof m365CapacityAdmissionSchema>;
export type M365AdminConsentCallbackQueryDTO = z.infer<
  typeof m365AdminConsentCallbackQuerySchema
>;
export type M365LifecycleEvent = z.infer<typeof m365LifecycleEventSchema>;
export type M365LifecycleNotificationDTO = z.infer<typeof m365LifecycleNotificationSchema>;
export type M365ContentNotificationDTO = z.infer<typeof m365ContentNotificationSchema>;
